<template>
    <v-expansion-panels>
        <v-expansion-panel class="rounded-xl">
            <v-expansion-panel-header class="rounded-xl" color="#6499faff" style="color: white;">
                {{ $t('form.userPayment') }}
                <span class="ml-5">{{ userMembership.startDate && userMembership.endDate ? `${getDate(userMembership.startDate)} ~ ${getDate(userMembership.endDate)}` : '' }}</span>
                <span v-if="userMembership.membership" class="ml-5">{{ userMembership.membership.type && userMembership.membership.name ? `${userMembership.membership.type} - ${userMembership.membership.name}` : '' }}</span>
                <span v-if="userMembership.sportClass" class="ml-5">{{ userMembership.sportClass.name && userMembership.sportClass.sport.name ? `${userMembership.sportClass.sport.name} - ${userMembership.sportClass.name}` : '' }}</span>
                <span class="ml-5" :style="userMembership.pendingAmount > 0 ? {color: 'red', fontWeight: 'bold'} : {}">{{ userMembership.pendingAmount ? ($t('form.pending') + ': ' + userMembership.pendingAmount + '€') : ($t('form.pending') + ': 0€') }}</span>
                <span>{{ userMembership.discount ? ($t('form.discount') + ': ' + userMembership.discount + '€') : ($t('form.discount') + ': 0€') }}</span>
            </v-expansion-panel-header>
            <v-expansion-panel-content color="grey lighten-4 rounded-b-xl">
                <div class="pa-4">
                    <div class="d-flex justify-end my-2">
                        <v-btn small color="primary" @click="printReceipt">
                            <v-icon left>mdi-printer</v-icon>
                            {{ $t('button.print') }}
                        </v-btn>
                    </div>
                    <v-form :readonly="readOnly">
                        <v-row>
                            <v-col cols="6" lg="4">
                                <v-text-field
                                    v-model="userMembership.pendingAmount"
                                    :type="Number"
                                    :label="$t('form.pendingAmount')"
                                    :clearable="clearable"
                                />
                            </v-col>
                            <v-col cols="6" lg="4">
                                <v-text-field
                                    v-model="userMembership.discount"
                                    :label="$t('form.discount')"
                                    :type="Number"
                                    :clearable="clearable"
                                />
                            </v-col>
                            <v-col cols="6" lg="4">
                                <v-autocomplete
                                    v-model="selectedSport"
                                    :items="sports"
                                    :item-text="sport => sport.name"
                                    :loading="loading"
                                    :label="$t('form.selectSport')"
                                    hide-details
                                    chips
                                    clearable
                                    return-object
                                />
                            </v-col>
                            <v-col cols="6" lg="4">
                                <v-autocomplete
                                    v-model="userMembership.sportClass"
                                    :items="filteredSportClasses"
                                    :item-text="sportClass => `${sportClass.sport.name} - ${sportClass.name}`"
                                    :loading="loading"
                                    :label="$t('form.selectSportClass')"
                                    :disabled="!selectedSport"
                                    hide-details
                                    chips
                                    clearable
                                    return-object
                                />
                            </v-col>
                            <v-col cols="6" lg="4">
                                <v-autocomplete
                                    v-model="userMembership.membership"
                                    :items="memberships"
                                    :item-text="membership => `${membership.name} - ${membership.type ? membership.type : ''} - ${membership.price}€`"
                                    :loading="loading"
                                    :label="$t('form.selectMembership')"
                                    hide-details
                                    chips
                                    clearable
                                    return-object
                                >
                                    <template v-slot:selection="data">
                                        <v-chip
                                            v-bind="data.attrs"
                                            :input-value="data.selected"
                                        >
                                            {{ data.item.name }} - {{ data.item.type ? data.item.type : '' }} - {{ data.item.price }}€
                                        </v-chip>
                                    </template>
                                </v-autocomplete>
                            </v-col>
                            <v-col cols="6" lg="4">
                                <date-time-picker
                                    :date-label="$t('form.startDate')"
                                    :date="startDate"
                                    @setDate="setStartDate"
                                />
                            </v-col>
                            <v-col cols="6" lg="3">
                                <date-time-picker
                                    :date-label="$t('form.endDate')"
                                    :date="endDate"
                                    @setDate="setEndDate"
                                />
                            </v-col>
                        </v-row>
                    </v-form>
                </div>
            </v-expansion-panel-content>
        </v-expansion-panel>
    </v-expansion-panels>
</template>

<script>
    import userService from '@/services/user'
    import membershipService from '@/services/membership'
    import sportClassService from '@/services/sportClass'
    import sportService from '@/services/sport'
    import DateTimePicker from '@/components/DateTimePicker'
    import momentTimezone from 'moment-timezone'
    import receiptLogo from '@/assets/logo_kiddy_motion-01.png'

    export default {
        name: 'UserMembershipForm',
        components: { DateTimePicker },
        props: {
            userMembership: {
                type: Object,
                required: true
            }
        },
        data() {
            return {
                dialog: false,
                message: null,
                type: null,
                mode: null,
                readOnly: false,
                clearable: true,
                loading: false,
                startDate: null,
                endDate: null,
                sports: [],
                selectedSport: this.userMembership && this.userMembership.sportClass && this.userMembership.sportClass.sport
                    ? this.userMembership.sportClass.sport
                    : null,
                sportClasses: [],
                memberships: [],
                users: [],
                mandatoryField: [
                    v => !!v || 'Field is required'
                ],
                search: null
            }
        },
        computed: {
            filteredSportClasses() {
                if (!this.selectedSport) {
                    return []
                }
                return this.sportClasses.filter(sc => sc.sport && sc.sport.id === this.selectedSport.id)
            }
        },
        watch: {
            'userMembership.sportClass': {
                handler(sportClass) {
                    this.selectedSport = sportClass && sportClass.sport ? sportClass.sport : null
                },
                immediate: true
            }
        },
        created() {
            this.mode = this.$route.params.mode
            if (this.mode === 'show') {
                this.readOnly = true
                this.clearable = false
            }
            if (this.$route.params.userId) {
                userService.getUser(this.$route.params.userId).then(resp => {
                    this.userMembership.user = resp.data
                    this.startDate = this.userMembership.startDate
                        ? momentTimezone(this.userMembership.startDate).tz('Europe/Athens').format('YYYY-MM-DD').toString()
                        : null
                    this.endDate = this.userMembership.endDate
                        ? momentTimezone(this.userMembership.endDate).tz('Europe/Athens').format('YYYY-MM-DD').toString()
                        : null
                })
            }
            this.fetchAllSports()
            this.fetchAllSportClasses()
            this.fetchAllMemberships()
        },
        methods: {
            setStartDate(date) {
                this.startDate = date || null
                this.userMembership.startDate = this.startDate
                    ? momentTimezone(this.startDate).tz('UTC').toISOString()
                    : null
            },
            setEndDate(date) {
                this.endDate = date || null
                this.userMembership.endDate = this.endDate
                    ? momentTimezone(this.endDate).tz('UTC').toISOString()
                    : null
            },
            openDialog(data) {
                this.dialog = true
            },
            setAlert(message, type) {
                this.showAlert = true
                this.message = message
                this.type = type
                setTimeout(() => { this.showAlert = false }, 7000)
            },
            fetchAllSports() {
                sportService.getSports().then(resp => {
                    this.sports = resp.data.content
                })
            },
            fetchAllSportClasses() {
                sportClassService.getSportClasses().then(resp => {
                    this.sportClasses = resp.data.content
                })
            },
            fetchAllMemberships() {
                membershipService.getMemberships().then(resp => {
                    this.memberships = resp.data.content
                })
            },
            getDate(date) {
                return momentTimezone(date).tz('Europe/Athens').format('YYYY-MM-DD').toString()
            },
            printReceipt() {
                const user = this.userMembership && this.userMembership.user ? this.userMembership.user : {}
                const membership = this.userMembership && this.userMembership.membership ? this.userMembership.membership : {}
                const sportClass = this.userMembership && this.userMembership.sportClass ? this.userMembership.sportClass : {}
                const sportName = sportClass && sportClass.sport ? sportClass.sport.name : ''

                const formatValue = (value) => {
                    if (value === null || value === undefined || value === '') {
                        return '-'
                    }
                    return String(value)
                }

                const formatDate = (value) => {
                    if (!value) {
                        return '-'
                    }
                    return momentTimezone(value).tz('Europe/Athens').format('DD/MM/YYYY').toString()
                }

                const labels = {
                    title: this.$t('receipt.title'),
                    userInfo: this.$t('receipt.userInfo'),
                    membershipInfo: this.$t('receipt.membershipInfo'),
                    paymentInfo: this.$t('receipt.paymentInfo'),
                    signatureSection: this.$t('receipt.signatureSection'),
                    userSignature: this.$t('receipt.userSignature'),
                    companySignature: this.$t('receipt.companySignature'),
                    companyStamp: this.$t('receipt.companyStamp'),
                    name: this.$t('field.name'),
                    surname: this.$t('field.surname'),
                    email: this.$t('field.email'),
                    mobile: this.$t('field.mobilePhone'),
                    membership: this.$t('home.table.membership'),
                    type: this.$t('membership.type'),
                    price: this.$t('price'),
                    sportClass: this.$t('home.table.sportClass'),
                    startDate: this.$t('form.startDate'),
                    endDate: this.$t('form.endDate'),
                    discount: this.$t('form.discount'),
                    pendingAmount: this.$t('form.pendingAmount'),
                    finalAmount: this.$t('receipt.finalAmount'),
                    generatedOn: this.$t('receipt.generatedOn')
                }
                const toNumber = (value) => {
                    const numericValue = Number(value)
                    return Number.isFinite(numericValue) ? numericValue : 0
                }
                const finalAmount = toNumber(membership.price) - (toNumber(this.userMembership.pendingAmount) + toNumber(this.userMembership.discount))

                const html = `
                    <html>
                        <head>
                            <title>${labels.title}</title>
                            <style>
                                :root {
                                    --receipt-blue: #00A0C6;
                                    --receipt-blue-deep: #007e9c;
                                    --receipt-orange: #ec6d3b;
                                    --receipt-green: #73866e;
                                    --receipt-ink: #243042;
                                    --receipt-muted: #6f7b8f;
                                    --receipt-line: #dde4f0;
                                    --receipt-soft: #f4f7fc;
                                    --receipt-summary-soft: #e9f7fb;
                                }
                                * { box-sizing: border-box; }
                                body {
                                    font-family: Roboto, Arial, sans-serif;
                                    margin: 0;
                                    padding: 28px;
                                    color: var(--receipt-ink);
                                    -webkit-print-color-adjust: exact;
                                    print-color-adjust: exact;
                                    background:
                                        radial-gradient(circle at top left, rgba(95, 143, 232, 0.16), transparent 28%),
                                        linear-gradient(180deg, #f7f9fd 0%, #eef3fb 100%);
                                }
                                .receipt-paper {
                                    max-width: 820px;
                                    margin: 0 auto;
                                    background: #fff;
                                    border: 1px solid rgba(95, 143, 232, 0.14);
                                    border-radius: 24px;
                                    overflow: hidden;
                                    box-shadow: 0 24px 60px rgba(38, 67, 122, 0.14);
                                }
                                .receipt-paper-topbar {
                                    height: 12px;
                                    background: linear-gradient(90deg, var(--receipt-orange), var(--receipt-blue));
                                }
                                .receipt-paper-body {
                                    padding: 28px;
                                    display: flex;
                                    flex-direction: column;
                                    min-height: calc(100vh - 90px);
                                }
                                .receipt-hero {
                                    display: flex;
                                    align-items: center;
                                    justify-content: space-between;
                                    gap: 24px;
                                    margin-bottom: 22px;
                                }
                                .receipt-hero-copy {
                                    flex: 1;
                                }
                                .receipt-eyebrow {
                                    display: inline-block;
                                    padding: 6px 12px;
                                    border-radius: 999px;
                                    background: rgba(95, 143, 232, 0.1);
                                    color: var(--receipt-blue-deep);
                                    font-size: 12px;
                                    font-weight: 700;
                                    letter-spacing: 0.08em;
                                    text-transform: uppercase;
                                }
                                .receipt-title {
                                    margin: 14px 0 8px;
                                    font-size: 30px;
                                    line-height: 1.1;
                                    font-weight: 800;
                                    color: var(--receipt-ink);
                                }
                                .receipt-subtitle {
                                    margin: 0;
                                    color: var(--receipt-muted);
                                    font-size: 14px;
                                    line-height: 1.6;
                                }
                                .receipt-logo-wrap {
                                    flex: 0 0 210px;
                                    text-align: center;
                                    padding: 18px;
                                    border-radius: 20px;
                                    background: linear-gradient(180deg, rgba(95, 143, 232, 0.08), rgba(236, 109, 59, 0.06));
                                }
                                .receipt-logo {
                                    display: block;
                                    max-width: 170px;
                                    margin: 0 auto;
                                }
                                .receipt-meta-strip {
                                    display: grid;
                                    grid-template-columns: minmax(0, 1fr);
                                    gap: 14px;
                                }
                                .receipt-first-page {
                                    display: grid;
                                    gap: 22px;
                                }
                                .receipt-first-header {
                                    display: grid;
                                    grid-template-columns: minmax(220px, 280px) minmax(0, 1fr);
                                    gap: 18px;
                                    align-items: center;
                                }
                                .receipt-meta-card {
                                    padding: 14px 16px;
                                    border-radius: 16px;
                                    background: var(--receipt-soft);
                                    border: 1px solid var(--receipt-line);
                                    max-width: 320px;
                                }
                                .receipt-meta-label {
                                    display: block;
                                    margin-bottom: 4px;
                                    font-size: 11px;
                                    font-weight: 700;
                                    letter-spacing: 0.08em;
                                    text-transform: uppercase;
                                    color: var(--receipt-muted);
                                }
                                .receipt-meta-value {
                                    font-size: 16px;
                                    font-weight: 700;
                                    color: var(--receipt-ink);
                                }
                                .receipt-content-grid {
                                    display: grid;
                                    grid-template-columns: minmax(0, 1fr);
                                    gap: 14px;
                                    align-items: start;
                                }
                                .receipt-stack {
                                    display: grid;
                                    gap: 14px;
                                }
                                .receipt-panel {
                                    border: 1px solid var(--receipt-line);
                                    border-radius: 20px;
                                    overflow: hidden;
                                    background: #fff;
                                    break-inside: avoid;
                                    page-break-inside: avoid;
                                }
                                .receipt-panel-head {
                                    display: flex;
                                    align-items: center;
                                    justify-content: space-between;
                                    gap: 12px;
                                    padding: 14px 18px;
                                    background: #00A0C6;
                                    border-bottom: 1px solid #0088a8;
                                }
                                .receipt-section-title {
                                    font-size: 15px;
                                    font-weight: 800;
                                    color: #fff;
                                }
                                .receipt-panel-body {
                                    padding: 10px 18px 14px;
                                }
                                .receipt-row {
                                    display: grid;
                                    grid-template-columns: minmax(120px, 180px) minmax(0, 1fr);
                                    gap: 16px;
                                    align-items: center;
                                    padding: 10px 0;
                                    border-bottom: 1px dashed var(--receipt-line);
                                }
                                .receipt-row:last-child {
                                    border-bottom: none;
                                }
                                .receipt-label {
                                    font-size: 13px;
                                    font-weight: 700;
                                    color: var(--receipt-muted);
                                }
                                .receipt-value {
                                    text-align: right;
                                    font-size: 14px;
                                    font-weight: 600;
                                    color: var(--receipt-ink);
                                    word-break: break-word;
                                }
                                .receipt-summary-card {
                                    border-radius: 22px;
                                    background: linear-gradient(180deg, #ffffff 0%, var(--receipt-summary-soft) 100%);
                                    color: var(--receipt-ink);
                                    padding: 20px;
                                    border: 1px solid rgba(0, 160, 198, 0.22);
                                    box-shadow: 0 16px 35px rgba(0, 160, 198, 0.12);
                                    break-inside: avoid;
                                    page-break-inside: avoid;
                                    margin-top: 14px;
                                }
                                .receipt-summary-title {
                                    margin: 0 0 16px;
                                    font-size: 15px;
                                    font-weight: 800;
                                    letter-spacing: 0.04em;
                                    text-transform: uppercase;
                                    color: #00A0C6;
                                }
                                .receipt-summary-row {
                                    display: flex;
                                    align-items: center;
                                    justify-content: space-between;
                                    gap: 14px;
                                    padding: 10px 0;
                                    border-bottom: 1px solid rgba(0, 160, 198, 0.14);
                                }
                                .receipt-summary-row:last-of-type {
                                    border-bottom: none;
                                }
                                .receipt-summary-label {
                                    font-size: 13px;
                                    color: var(--receipt-muted);
                                }
                                .receipt-summary-value {
                                    font-size: 15px;
                                    font-weight: 700;
                                    color: var(--receipt-ink);
                                    white-space: nowrap;
                                }
                                .receipt-summary-total {
                                    margin-top: 16px;
                                    padding: 16px 18px;
                                    border-radius: 18px;
                                    background: linear-gradient(180deg, #00A0C6 0%, #007e9c 100%);
                                    box-shadow: inset 0 1px 0 rgba(255, 255, 255, 0.18);
                                }
                                .receipt-summary-total-label {
                                    display: block;
                                    margin-bottom: 6px;
                                    font-size: 12px;
                                    font-weight: 700;
                                    text-transform: uppercase;
                                    letter-spacing: 0.08em;
                                    color: rgba(255, 255, 255, 0.78);
                                }
                                .receipt-summary-total-value {
                                    font-size: 30px;
                                    line-height: 1;
                                    font-weight: 800;
                                    color: #fff;
                                }
                                .receipt-signature-section {
                                    margin-top: auto;
                                    padding-top: 22px;
                                }
                                .receipt-signature-title {
                                    margin: 0 0 14px;
                                    font-size: 14px;
                                    font-weight: 800;
                                    letter-spacing: 0.06em;
                                    text-transform: uppercase;
                                    color: #00A0C6;
                                }
                                .receipt-signature-grid {
                                    display: grid;
                                    grid-template-columns: repeat(3, minmax(0, 1fr));
                                    gap: 16px;
                                }
                                .receipt-signature-card {
                                    min-height: 118px;
                                    padding: 16px;
                                    border: 1px dashed rgba(0, 160, 198, 0.35);
                                    border-radius: 18px;
                                    background: linear-gradient(180deg, #fff 0%, #f9fdfe 100%);
                                    display: flex;
                                    flex-direction: column;
                                    justify-content: flex-end;
                                }
                                .receipt-signature-label {
                                    font-size: 12px;
                                    font-weight: 700;
                                    color: var(--receipt-muted);
                                    margin-bottom: 18px;
                                    text-transform: uppercase;
                                    letter-spacing: 0.06em;
                                }
                                .receipt-signature-line {
                                    height: 1px;
                                    background: rgba(36, 48, 66, 0.22);
                                    margin-bottom: 8px;
                                }
                                .receipt-signature-caption {
                                    font-size: 12px;
                                    font-weight: 700;
                                    color: var(--receipt-ink);
                                }
                                .receipt-stamp-box {
                                    align-items: center;
                                    justify-content: center;
                                }
                                .receipt-stamp-circle {
                                    width: 88px;
                                    height: 88px;
                                    border-radius: 50%;
                                    border: 2px dashed rgba(0, 160, 198, 0.5);
                                    display: flex;
                                    align-items: center;
                                    justify-content: center;
                                    text-align: center;
                                    padding: 12px;
                                    color: #00A0C6;
                                    font-size: 11px;
                                    font-weight: 800;
                                    letter-spacing: 0.05em;
                                    text-transform: uppercase;
                                }
                                .receipt-footer {
                                    margin-top: 22px;
                                    padding-top: 16px;
                                    border-top: 1px solid var(--receipt-line);
                                    text-align: center;
                                    font-size: 12px;
                                    color: var(--receipt-muted);
                                }
                                @media (max-width: 720px) {
                                    body { padding: 12px; }
                                    .receipt-paper-body { padding: 20px; }
                                    .receipt-first-header {
                                        grid-template-columns: 1fr;
                                    }
                                    .receipt-hero,
                                    .receipt-meta-strip {
                                        grid-template-columns: 1fr;
                                        display: grid;
                                    }
                                    .receipt-signature-grid {
                                        grid-template-columns: 1fr;
                                    }
                                    .receipt-hero {
                                        gap: 16px;
                                    }
                                    .receipt-logo-wrap {
                                        justify-self: start;
                                        width: 100%;
                                        max-width: 260px;
                                    }
                                    .receipt-meta-card {
                                        max-width: none;
                                    }
                                    .receipt-row {
                                        grid-template-columns: 1fr;
                                        gap: 6px;
                                    }
                                    .receipt-value {
                                        text-align: left;
                                    }
                                }
                                @media print {
                                    body {
                                        padding: 6mm;
                                        font-size: 12px;
                                    }
                                    .receipt-paper {
                                        max-width: none;
                                    }
                                    .receipt-paper-body {
                                        padding: 16px;
                                    }
                                    .receipt-first-page,
                                    .receipt-stack,
                                    .receipt-content-grid {
                                        gap: 14px;
                                    }
                                    .receipt-first-header,
                                    .receipt-meta-strip {
                                        gap: 12px;
                                    }
                                    .receipt-hero {
                                        margin-bottom: 10px;
                                    }
                                    .receipt-logo-wrap {
                                        padding: 10px;
                                    }
                                    .receipt-logo {
                                        max-width: 130px;
                                    }
                                    .receipt-meta-card {
                                        padding: 10px 12px;
                                    }
                                    .receipt-meta-label {
                                        font-size: 10px;
                                        margin-bottom: 2px;
                                    }
                                    .receipt-meta-value {
                                        font-size: 14px;
                                    }
                                    .receipt-panel-head {
                                        padding: 10px 14px;
                                    }
                                    .receipt-section-title,
                                    .receipt-summary-title {
                                        font-size: 13px;
                                    }
                                    .receipt-panel-body {
                                        padding: 8px 14px 12px;
                                    }
                                    .receipt-row {
                                        padding: 8px 0;
                                        gap: 10px;
                                    }
                                    .receipt-label,
                                    .receipt-value,
                                    .receipt-summary-label,
                                    .receipt-summary-value {
                                        font-size: 12px;
                                    }
                                    .receipt-summary-card {
                                        padding: 16px;
                                    }
                                    .receipt-summary-title {
                                        margin-bottom: 12px;
                                    }
                                    .receipt-summary-row {
                                        padding: 8px 0;
                                    }
                                    .receipt-summary-total {
                                        margin-top: 12px;
                                        padding: 12px 14px;
                                    }
                                    .receipt-summary-total-label {
                                        font-size: 10px;
                                        margin-bottom: 4px;
                                    }
                                    .receipt-summary-total-value {
                                        font-size: 24px;
                                    }
                                    .receipt-signature-section {
                                        padding-top: 14px;
                                    }
                                    .receipt-signature-title {
                                        margin-bottom: 10px;
                                        font-size: 12px;
                                    }
                                    .receipt-signature-grid {
                                        gap: 10px;
                                    }
                                    .receipt-signature-card {
                                        min-height: 92px;
                                        padding: 12px;
                                    }
                                    .receipt-signature-label,
                                    .receipt-signature-caption,
                                    .receipt-stamp-circle {
                                        font-size: 10px;
                                    }
                                    .receipt-stamp-circle {
                                        width: 72px;
                                        height: 72px;
                                    }
                                    .receipt-footer {
                                        margin-top: 14px;
                                        padding-top: 10px;
                                        font-size: 10px;
                                    }
                                    @page {
                                        size: A4;
                                        margin: 6mm;
                                    }
                                }
                            </style>
                        </head>
                        <body onload="setTimeout(function(){ window.print(); window.close(); }, 100)">
                            <div class="receipt-paper">
                                <div class="receipt-paper-topbar"></div>
                                <div class="receipt-paper-body">
                                    <div class="receipt-first-page">
                                        <div class="receipt-first-header">
                                            <div class="receipt-hero">
                                                <div class="receipt-logo-wrap">
                                                    <img class="receipt-logo" src="${receiptLogo}" alt="Kiddy Motion Logo" />
                                                </div>
                                            </div>

                                            <div class="receipt-meta-strip">
                                                <div class="receipt-meta-card">
                                                    <span class="receipt-meta-label">${labels.generatedOn}</span>
                                                    <span class="receipt-meta-value">${momentTimezone().tz('Europe/Athens').format('DD/MM/YYYY HH:mm')}</span>
                                                </div>
                                            </div>
                                        </div>
                                        <div class="receipt-stack">
                                            <div class="receipt-panel">
                                                <div class="receipt-panel-head">
                                                    <div class="receipt-section-title">${labels.userInfo}</div>
                                                </div>
                                                <div class="receipt-panel-body">
                                                    <div class="receipt-row"><span class="receipt-label">${labels.name}</span><span class="receipt-value">${formatValue(user.name)}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.surname}</span><span class="receipt-value">${formatValue(user.lastName)}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.email}</span><span class="receipt-value">${formatValue(user.email)}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.mobile}</span><span class="receipt-value">${formatValue(user.mobile)}</span></div>
                                                </div>
                                            </div>

                                            <div class="receipt-panel">
                                                <div class="receipt-panel-head">
                                                    <div class="receipt-section-title">${labels.membershipInfo}</div>
                                                </div>
                                                <div class="receipt-panel-body">
                                                    <div class="receipt-row"><span class="receipt-label">${labels.type}</span><span class="receipt-value">${formatValue(membership.type)}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.sportClass}</span><span class="receipt-value">${formatValue(sportName && sportClass.name ? `${sportName} - ${sportClass.name}` : '')}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.startDate}</span><span class="receipt-value">${formatDate(this.userMembership.startDate || this.startDate)}</span></div>
                                                    <div class="receipt-row"><span class="receipt-label">${labels.endDate}</span><span class="receipt-value">${formatDate(this.userMembership.endDate || this.endDate)}</span></div>
                                                </div>
                                            </div>
                                        </div>
                                    </div>

                                    <div class="receipt-summary-card">
                                        <div class="receipt-summary-title">${labels.paymentInfo}</div>
                                        <div class="receipt-summary-row"><span class="receipt-summary-label">${labels.price}</span><span class="receipt-summary-value">${formatValue(membership.price)}€</span></div>
                                        <div class="receipt-summary-row"><span class="receipt-summary-label">${labels.discount}</span><span class="receipt-summary-value">${formatValue(this.userMembership.discount)}€</span></div>
                                        <div class="receipt-summary-row"><span class="receipt-summary-label">${labels.pendingAmount}</span><span class="receipt-summary-value">${formatValue(this.userMembership.pendingAmount)}€</span></div>
                                        <div class="receipt-summary-total">
                                            <span class="receipt-summary-total-label">${labels.finalAmount}</span>
                                            <span class="receipt-summary-total-value">${formatValue(finalAmount)}€</span>
                                        </div>
                                    </div>

                                    <div class="receipt-signature-section">
                                        <div class="receipt-signature-title">${labels.signatureSection}</div>
                                        <div class="receipt-signature-grid">
                                            <div class="receipt-signature-card">
                                                <div class="receipt-signature-label">${labels.userSignature}</div>
                                                <div class="receipt-signature-line"></div>
                                                <div class="receipt-signature-caption">${labels.userSignature}</div>
                                            </div>
                                            <div class="receipt-signature-card">
                                                <div class="receipt-signature-label">${labels.companySignature}</div>
                                                <div class="receipt-signature-line"></div>
                                                <div class="receipt-signature-caption">${labels.companySignature}</div>
                                            </div>
                                            <div class="receipt-signature-card receipt-stamp-box">
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            </div>
                        </body>
                    </html>`

                const win = window.open('', '_blank', 'width=900,height=700')
                if (!win) {
                    if (typeof this.setAlert === 'function') {
                        this.setAlert('Popup blocked. Please allow popups to print.', 'error')
                    } else {
                        alert('Popup blocked. Please allow popups to print.')
                    }
                    return
                }

                win.document.open()
                win.document.write(html)
                win.document.close()
            }
        }

    }
</script>
<style>
.tox .tox-editor-header {
    z-index: 0 !important;
}
.tox .tox-statusbar {
    display: none !important;
}

.tox .tox-toolbar--scrolling {
    flex-wrap: wrap !important;
}
.tox.tox-tinymce.tox-platform-touch {
    height: 40vh !important;
    min-height: 350px;
}
</style>
<style scoped>
.v-menu__content{
    background-color: white!important;
    overflow-y: hidden!important;
}
.body-label {
    color: rgba(0, 0, 0, 0.6);
}
.body-editor {
    margin-bottom: 2vh;
}
.edit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: flex-end;
}

.submit-button-wrapper {
    width: 100%;
    display: flex;
    justify-content: center;
    margin-top: 2vh;
}

.img-preview {
    max-width: 70%;
    margin: 0 auto;
}

.image-input {
    padding: 20px;
    width: 100%;
    border-style: groove;
    border: #D3D3D3 solid 2px;
    border-radius: 15px 50px;
    margin-top: 1vh;
}

.green-border {
    border-color: green;
}

.delete-btn {
    display: flex;
    justify-content: flex-end;
}
</style>
